import { createFileRoute } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { Button, Container, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { getUriWithHash } from '@social-garden/utils/helpers.ts';
import {
  brandDocument,
  updateBrandDocument,
  updateBrandLogoDocument,
} from '@social-garden/api/documents/brand.ts';
import {
  UpdateBrandFieldValues,
  UpdateBrandSchema,
} from '@/constants/ValidationSchema.ts';
import ImageCropField from '@/components/ImageCropField.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import BrandNameField from '@/components/BrandNameField.tsx';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';

export const Route = createFileRoute('/_manager/_app/brands/$id/update')({
  component: UpdateBrand,
});

function UpdateBrand() {
  const { id } = Route.useParams();
  const { t } = useTranslation(['manager']);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(brandDocument, {
    variables: {
      id,
    },
  });
  const [updateBrand, { loading: updateBrandLoading }] =
    useMutation(updateBrandDocument);
  const [updateBrandLogo] = useMutation(updateBrandLogoDocument);

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isValid, isSubmitting },
  } = useForm<UpdateBrandFieldValues>({
    mode: 'all',
    resolver: zodResolver(UpdateBrandSchema),
    defaultValues: {
      name: data.brand.name,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting });

  const handleOnValid = useCallback(
    async ({ name }: UpdateBrandFieldValues) => {
      const response = await updateBrand({
        variables: {
          input: {
            id,
            name,
          },
        },
      });
      if (response.data?.updateBrand) {
        enqueueSnackbar(t('brand.update.successText'), {
          variant: 'success',
        });
        await navigate({ to: '/brands' });
      }
    },
    [navigate, enqueueSnackbar, id, updateBrand, t],
  );

  const handleOnImageChange = useCallback(
    async (logo: File) => {
      const response = await updateBrandLogo({
        variables: {
          input: {
            id,
            logo,
          },
        },
      });
      if (response.data?.updateBrandLogo) {
        enqueueSnackbar(t('brand.update.logo.successText'), {
          variant: 'success',
        });
        await navigate({ to: '/brands' });
      }
    },
    [navigate, updateBrandLogo, id, enqueueSnackbar, t],
  );

  return (
    <Container
      maxWidth="md"
      component="form"
      noValidate
      onSubmit={handleSubmit(handleOnValid)}>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('brand.update.title')}</PageTitle>
        </Grid>
        <Grid container size={12} spacing={2}>
          <Grid size={12}>
            <ImageCropField
              fullWidth
              label={t('brand.update.logo.label')}
              previewSrc={getUriWithHash(data.brand.logo)}
              width={500}
              height={500}
              cropShape="round"
              onChange={handleOnImageChange}
            />
          </Grid>
          <Grid size={12}>
            <Controller<UpdateBrandFieldValues, 'name'>
              name="name"
              control={control}
              render={BrandNameField}
            />
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              loading={updateBrandLoading}
              disabled={!isValid}>
              {t('brand.update.submitButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
